import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuCalendar, LuClock } from 'react-icons/lu';
import AuthContext from '../shared/AuthContext';
import { invalidateRealtime } from '../../realtime/realtimeBus';

function formatDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return null;
  }
}

export default function TeamRequestList({
  orgId = null,
  status = 'PENDING',
  compact = false,
  prefetched,
  onUpdated,
  emptyMessage = 'No pending requests.',
}) {
  const { apiClient } = useContext(AuthContext);
  const [items, setItems] = useState(prefetched || []);
  const [loading, setLoading] = useState(!prefetched);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (orgId) {
        const q = status ? `?status=${encodeURIComponent(status)}` : '';
        const res = await apiClient.get(`/api/organizations/${orgId}/requests${q}`);
        setItems(res.data || []);
      } else {
        const res = await apiClient.get('/api/organizations/pending-requests?limit=20');
        const all = res.data?.items || [];
        setItems(status ? all.filter((r) => r.status === status) : all);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, orgId, status]);

  useEffect(() => {
    if (prefetched) {
      setItems(prefetched);
      setLoading(false);
      return;
    }
    load();
  }, [load, prefetched]);

  const respond = async (row, action) => {
    setActingId(row.id);
    try {
      if (row.requestType === 'WORKSPACE') {
        const invitationId = row.invitationId ?? row.id;
        if (action === 'ACCEPT') {
          await apiClient.post(`/api/organizations/workspace-invitations/${invitationId}/accept`);
        } else {
          await apiClient.post(`/api/organizations/workspace-invitations/${invitationId}/decline`);
        }
      } else {
        const targetOrg = row.organizationId ?? orgId;
        if (!targetOrg) return;
        await apiClient.patch(`/api/organizations/${targetOrg}/requests/${row.id}`, { action });
      }
      await load();
      invalidateRealtime('team-request');
      invalidateRealtime('notification');
      onUpdated?.();
    } finally {
      setActingId(null);
    }
  };

  if (loading && items.length === 0) {
    return <p className="teams-sub">{compact ? '…' : 'Loading requests…'}</p>;
  }

  if (items.length === 0) {
    return <p className="teams-sub">{emptyMessage}</p>;
  }

  return (
    <ul className={`teams-request-list ${compact ? 'teams-request-list--compact' : ''}`}>
      {items.map((r) => {
        const rowOrgId = r.organizationId ?? orgId;
        return (
        <li key={`${r.requestType}-${r.id}`} className="teams-request-item">
          <div className="teams-request-main">
            <span className="teams-badge teams-badge--muted">{r.requestType}</span>
            <strong>{r.title}</strong>
            {r.organizationName && (
              <p className="teams-sub">{r.organizationName}</p>
            )}
            {r.summary && <p className="teams-sub">{r.summary}</p>}
            {!compact && r.invitedByName && (
              <p className="teams-sub">From {r.invitedByName}</p>
            )}
            {!compact && r.createdAt && (
              <p className="teams-sub">
                <LuCalendar aria-hidden className="teams-inline-icon" />
                Invited {formatDateTime(r.createdAt)}
              </p>
            )}
            {!compact && r.respondedAt && (
              <p className="teams-sub">
                <LuClock aria-hidden className="teams-inline-icon" />
                Responded {formatDateTime(r.respondedAt)}
              </p>
            )}
          </div>
          {r.status === 'PENDING' ? (
            <div className="teams-request-actions">
              <button
                type="button"
                className="teams-btn teams-btn--secondary"
                disabled={actingId === r.id}
                onClick={() => respond(r, 'DECLINE')}
              >
                Decline
              </button>
              <button
                type="button"
                className="teams-btn"
                disabled={actingId === r.id}
                onClick={() => respond(r, 'ACCEPT')}
              >
                Accept
              </button>
            </div>
          ) : (
            <span className="teams-badge">{r.status}</span>
          )}
          {!compact && r.requestType === 'ASSIGNMENT' && r.status === 'ACCEPTED' && rowOrgId && (
            <Link className="teams-link" to={`/teams/${rowOrgId}/assignments`}>View assignments</Link>
          )}
          {!compact && r.requestType === 'CHALLENGE' && r.status === 'ACCEPTED' && rowOrgId && (
            <Link className="teams-link" to={`/teams/${rowOrgId}/challenges`}>View challenges</Link>
          )}
        </li>
        );
      })}
    </ul>
  );
}
